import React, { useEffect, useState } from 'react'
import {
  Card,
  Button,
  Form,
  Alert,
  FloatingLabel,
  Modal
} from 'react-bootstrap'
import logoBePass from '../../assets/images/bepass_logo_default.png'
import { useForm } from 'react-hook-form'

import { apiCompany } from '../../config/apiCall'
import {
  getCanonicalCompanySlug,
  getCompanyProfileBySlug
} from '../../config/companyProfile'
import {
  getAccessOrigin,
  applyCurrentCompanyTheme,
  applySelectedCompany,
  clearAuthSession,
  consumeAuthRedirectNotice,
  getCurrentCompanyProfile,
  hasUsableAuthenticatedSession,
  getSelectedCompanySlug,
  storeAuthenticatedSession,
  type CompanySelection
} from '../../config/session'
import {
  schedulePreventiveSessionRefresh
} from '../../config/authFlow'
import { sanitizeCompanySlug } from '../../utils/companySlug'

type LoginFormData = {
  user: string
  password: string
}

type LoginRole = {
  companyId?: string
  applications?: {
    id?: number
  }
  role?: {
    name?: string
    roleId?: string | number
  }
}

const CONTINGENCY_APPLICATION_IDS = new Set([2, 3, 7])

const resolveCompanyPortalOrigin = (company: CompanySelection | null): string => {
  const companySlug = getCanonicalCompanySlug(
    company?.slug || getSelectedCompanySlug()
  )
  const profileOrigin = getCompanyProfileBySlug(companySlug).portalOrigin || ''
  const normalizedProfileOrigin = `${profileOrigin}`.trim().replace(/\/$/, '')

  if (normalizedProfileOrigin) {
    return normalizedProfileOrigin
  }

  const fallbackOrigin = getAccessOrigin().replace(/\/$/, '')
  return fallbackOrigin
}

const resolveCompanySlugForLogin = (
  company: CompanySelection | null
): string => {
  const companySlug = sanitizeCompanySlug(company?.slug)

  if (companySlug) {
    return companySlug
  }

  return getCanonicalCompanySlug(getSelectedCompanySlug())
}

const resolveLoginUrl = (company: CompanySelection | null): string => {
  const companySlug = resolveCompanySlugForLogin(company)
  const companyOrigin = resolveCompanyPortalOrigin(company)

  if (!companySlug) {
    return companyOrigin
  }

  const separator = companyOrigin.includes('?') ? '&' : '?'
  return `${companyOrigin}${separator}cmp=${encodeURIComponent(companySlug)}`
}

function Login() {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>()

  const [selectedCompany, setSelectedCompany] =
    useState<CompanySelection | null>(null)
  const [loginError, setLoginError] = useState({
    status: false,
    message: ''
  })
  const [authNotice, setAuthNotice] = useState({
    show: false,
    title: '',
    message: ''
  })

  useEffect(() => {
    const selectedSlug = getSelectedCompanySlug()
    const accessOrigin = getAccessOrigin().replace(/\/$/, '')
    const accessHostname = new URL(accessOrigin).hostname.toLowerCase()
    const storedSlug =
      `${sessionStorage.getItem('selected_cmp') || sessionStorage.getItem('company_slug') || ''}`.trim()

    const loadCompany = async () => {
      const authRedirectNotice = consumeAuthRedirectNotice()

      if (authRedirectNotice) {
        setAuthNotice({
          show: true,
          title: authRedirectNotice.title,
          message: authRedirectNotice.message
        })
      }

      if (
        hasUsableAuthenticatedSession() &&
        storedSlug &&
        storedSlug === selectedSlug
      ) {
        window.location.href = '/control'
        return
      }

      clearAuthSession()
      applyCurrentCompanyTheme()

      try {
        const response = await apiCompany.get('/companies/list', {
          headers: {
            'x-api-key': '',
            'x-tenant-id': '',
            'x-application': 'dash_contingencia'
          }
        })

        const companies = response?.data?.companies || []
        const currentProfile = getCurrentCompanyProfile()
        const resolvedCompany =
          companies.find((company: CompanySelection) => {
            const companySlug = getCanonicalCompanySlug(company.slug)
            const currentProfilePortalOrigin = currentProfile.portalOrigin
            const normalizedProfileOrigin = `${currentProfilePortalOrigin || ''}`.replace(/\/$/, '')

            if (companySlug && companySlug === selectedSlug) {
              return true
            }

            if (normalizedProfileOrigin && normalizedProfileOrigin === accessOrigin) {
              return true
            }

            if (companySlug && accessHostname.includes(`app-${companySlug}`)) {
              return true
            }

            if (
              companySlug &&
              accessHostname.includes(`contingencia-${companySlug}`)
            ) {
              return true
            }

            return false
          }) ||
          companies.find((c: CompanySelection) =>
            getCanonicalCompanySlug(c.slug) === getCanonicalCompanySlug(currentProfile.slug)
          ) ||
          null

        if (!resolvedCompany?.tenantId) {
          setLoginError({
            status: true,
            message: 'Clube não configurado para este acesso'
          })
          return
        }

        applySelectedCompany(resolvedCompany)
        sessionStorage.setItem(
          'company_origin',
          resolveCompanyPortalOrigin(resolvedCompany)
        )
        applyCurrentCompanyTheme()
        setSelectedCompany(resolvedCompany)
      } catch {
        setLoginError({
          status: true,
          message: 'Não foi possível carregar o clube deste acesso'
        })
      }
    }

    loadCompany()
  }, [])

  const onSubmit = async (data: LoginFormData) => {
    if (!selectedCompany?.tenantId) {
      setLoginError({
        status: true,
        message: 'Clube não configurado para este acesso'
      })
      return
    }

    setLoginError({
      status: false,
      message: ''
    })

    try {
      const loginOrigin = resolveLoginUrl(selectedCompany)
      const response = await apiCompany.post(
        '/firebase/host/login/with-slug',
        {
          url: loginOrigin,
          email: data.user.trim(),
          password: data.password
        },
        { validateStatus: () => true }
      )

      const body = response?.data || {}

      if (response.status < 200 || response.status >= 300) {
        setLoginError({
          status: true,
          message: 'Usuário ou senha inválidos'
        })
        return
      }

      const payload = body?.data || {}
      const loginRoles = (payload?.loginRoles || []) as LoginRole[]
      const currentProfile = getCurrentCompanyProfile()

      const apiKey = `${body?.token?.apiKey || ''}`.trim()
      const refreshToken = `${body?.refreshToken?.token || ''}`.trim()
      const tenantId = `${body?.tenant || selectedCompany.tenantId || ''}`.trim()
      const responseCompanyId = `${
        body?.companyData?.company_id ||
        body?.companyId ||
        ''
      }`.trim()
      const expectedCompanyId = `${
        selectedCompany.company_id ||
        selectedCompany.companyId ||
        currentProfile.companyId ||
        ''
      }`.trim()
      const roleCompanyId = expectedCompanyId || responseCompanyId
      const matchedRole = loginRoles.find(
        (item) =>
          item.companyId &&
          roleCompanyId &&
          item.companyId === roleCompanyId &&
          CONTINGENCY_APPLICATION_IDS.has(Number(item.applications?.id || 0))
      )
      const loginId = `${payload?.loginId || body?.id || ''}`.trim()
      const displayName = payload?.name || body?.name || 'Verne'

      if (!apiKey || !matchedRole || !tenantId) {
        setLoginError({
          status: true,
          message: 'Usuário sem acesso à contingência deste clube'
        })
        return
      }

      if (
        selectedCompany.tenantId &&
        tenantId &&
        tenantId !== selectedCompany.tenantId
      ) {
        setLoginError({
          status: true,
          message: 'Login retornou tenant diferente do clube selecionado'
        })
        return
      }

      if (
        expectedCompanyId &&
        responseCompanyId &&
        responseCompanyId !== expectedCompanyId
      ) {
        setLoginError({
          status: true,
          message: 'Login retornou empresa diferente do clube selecionado'
        })
        return
      }

      storeAuthenticatedSession({
        apiKey,
        refreshToken,
        tenantId,
        companyId: responseCompanyId || expectedCompanyId,
        role: matchedRole?.role?.name || 'default',
        roleId: `${matchedRole?.role?.roleId || ''}`,
        loginId,
        name: displayName
      })
      schedulePreventiveSessionRefresh()

      window.location.href = '/control'
    } catch {
      setLoginError({
        status: true,
        message: 'Usuário ou senha inválidos'
      })
    }
  }

  return (
    <React.Fragment>
      <Modal
        centered
        show={authNotice.show}
        className="auth-session-modal"
        backdrop="static"
        keyboard={false}
        onHide={() =>
          setAuthNotice((current) => ({
            ...current,
            show: false
          }))
        }
      >
        <Modal.Header>
          <Modal.Title>{authNotice.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="auth-session-modal__content">
            <p className="mb-0">{authNotice.message}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className="auth-session-modal__action"
            onClick={() =>
              setAuthNotice((current) => ({
                ...current,
                show: false
              }))
            }
          >
            Fazer login novamente
          </Button>
        </Modal.Footer>
      </Modal>
      <div className="auth-wrapper">
        <div className="auth-content">
          <Card className="borderless text-center">
            <Card.Body>
              <div className="mb-4">
                <img src={logoBePass} className="login-logo" alt="BePass" />
              </div>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FloatingLabel
                  className="mb-3"
                  controlId="floatingPassword"
                  label="Usuário"
                >
                  <Form.Control
                    type="text"
                    placeholder="Usuário"
                    {...register('user', { required: true })}
                  />
                  {errors.user && (
                    <Alert variant="danger">Por favor, informe um e-mail</Alert>
                  )}
                </FloatingLabel>

                <FloatingLabel
                  className="mb-3"
                  controlId="floatingPassword"
                  label="Senha"
                >
                  <Form.Control
                    type="password"
                    placeholder="Senha"
                    {...register('password', { required: true })}
                  />
                  {errors.password && (
                    <Alert variant="danger">Por favor, informe uma senha</Alert>
                  )}
                </FloatingLabel>

                {loginError.status && (
                  <Alert variant="danger">{loginError.message}</Alert>
                )}

                <Form.Group>
                  <Button
                    type="submit"
                    className="mb-0 mt-4 w-100 btn-success"
                  >
                    Entrar
                  </Button>
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </div>
      </div>
    </React.Fragment>
  )
}

export default Login
